/**
 * A very small markdown reader for Personalized Action Plan text.
 *
 * Plans are typed in the admin as plain markdown and rendered here as
 * React elements, never as HTML, so nothing a plan contains can inject
 * markup into the portal. Only the handful of shapes a plan needs are
 * understood: headings, paragraphs, bullet and numbered lists, quotes,
 * dividers, bold, and links. Anything else is shown as plain text.
 *
 * The same file lives at apps/admin/lib/plan-markdown.ts so the admin
 * preview matches what the customer sees. Keep the two in sync by hand
 * (the apps can't import each other, see case-state-v2 notes).
 */

export type InlineNode =
  | { type: 'text'; text: string }
  | { type: 'bold'; text: string }
  | { type: 'link'; text: string; href: string };

export type PlanBlock =
  | { type: 'heading'; level: 1 | 2 | 3; inlines: InlineNode[] }
  | { type: 'paragraph'; inlines: InlineNode[] }
  | { type: 'bullets'; items: InlineNode[][] }
  | { type: 'numbered'; items: InlineNode[][] }
  | { type: 'quote'; inlines: InlineNode[] }
  | { type: 'divider' };

const SAFE_HREF = /^(https?:\/\/|mailto:|tel:)/i;

/** Split a line of text into text / bold / link nodes. */
export function parseInlines(text: string): InlineNode[] {
  const out: InlineNode[] = [];
  // bold: **x**   link: [text](href)
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ type: 'text', text: text.slice(last, m.index) });
    if (m[1] !== undefined) {
      out.push({ type: 'bold', text: m[1] });
    } else if (SAFE_HREF.test(m[3])) {
      out.push({ type: 'link', text: m[2], href: m[3] });
    } else {
      // Unsafe scheme: keep the words, drop the link.
      out.push({ type: 'text', text: m[2] });
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ type: 'text', text: text.slice(last) });
  return out;
}

export function parsePlanMarkdown(markdown: string | null | undefined): PlanBlock[] {
  const blocks: PlanBlock[] = [];
  if (!markdown) return blocks;

  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  let paragraph: string[] = [];
  let list: { type: 'bullets' | 'numbered'; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: 'paragraph', inlines: parseInlines(paragraph.join(' ')) });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push({ type: list.type, items: list.items.map(parseInlines) });
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }

    if (/^-{3,}$/.test(trimmed)) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'divider' });
      continue;
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(trimmed);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({
        type: 'heading',
        level: heading[1].length as 1 | 2 | 3,
        inlines: parseInlines(heading[2].trim()),
      });
      continue;
    }

    const bullet = /^[-*•]\s+(.+)$/.exec(trimmed);
    if (bullet) {
      flushParagraph();
      if (!list || list.type !== 'bullets') {
        flushList();
        list = { type: 'bullets', items: [] };
      }
      list.items.push(bullet[1].trim());
      continue;
    }

    const numbered = /^\d+[.)]\s+(.+)$/.exec(trimmed);
    if (numbered) {
      flushParagraph();
      if (!list || list.type !== 'numbered') {
        flushList();
        list = { type: 'numbered', items: [] };
      }
      list.items.push(numbered[1].trim());
      continue;
    }

    const quote = /^>\s?(.*)$/.exec(trimmed);
    if (quote) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'quote', inlines: parseInlines(quote[1].trim()) });
      continue;
    }

    // A wrapped continuation of a list item stays in the item.
    if (list && /^\s{2,}/.test(raw)) {
      list.items[list.items.length - 1] += ' ' + trimmed;
      continue;
    }

    flushList();
    paragraph.push(trimmed);
  }

  flushParagraph();
  flushList();
  return blocks;
}

/** Plain-text version, used for previews and the email summary. */
export function inlinesToText(inlines: InlineNode[]): string {
  return inlines.map((n) => n.text).join('');
}
